import React, { useState, useEffect, useRef } from "react";
import { useUserAuth } from "../../context/userAuthContext";
import { db } from "../../firebase";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    setDoc,
    updateDoc,
    onSnapshot,
    arrayUnion,
    arrayRemove,
    serverTimestamp
} from "firebase/firestore";
import {
    Coffee,
    ShoppingCart,
    Plus,
    Minus,
    Tag,
    Filter,
    Search,
    X,
    ChevronDown,
    BookOpen,
    AlertTriangle,
    ShoppingBag,
    Loader
} from "lucide-react";

const ProductsSection = ({ placeId, placeName, placeSlug, cartId, onCartOpen }) => {
    const { user } = useUserAuth();
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [isProcessing, setIsProcessing] = useState(false);
    const [cartItemCount, setCartItemCount] = useState(0);

    // Add a ref to track if products need updating from cart
    const needsProductUpdate = useRef(false);

    // Cargar productos del restaurante
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);

                console.log("Obteniendo productos para el restaurante:", placeId);

                // Consulta todos los productos para filtrar manualmente
                const productsCollection = collection(db, "products");
                const q = query(productsCollection);
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    console.log("No se encontraron productos en la colección");
                    setProducts([]);
                    setFilteredProducts([]);
                    setLoading(false);
                    return;
                }

                console.log(`Se encontraron ${querySnapshot.size} productos en total`);

                const productsData = [];
                const categoriesSet = new Set();

                // Filtrar manualmente los productos que están asignados a este restaurante
                querySnapshot.forEach((doc) => {
                    const productData = doc.data();

                    // Verificar si el producto tiene asignaciones de restaurante
                    if (productData.restaurantAssignments && Array.isArray(productData.restaurantAssignments)) {
                        // Verificar si el producto está asignado a este restaurante
                        const isAssignedToThisPlace = productData.restaurantAssignments.some(
                            assignment => assignment.restaurantId === placeId
                        );

                        if (isAssignedToThisPlace) {
                            console.log(`Producto asignado encontrado: ${productData.name}`);

                            // Este producto está asignado a este restaurante
                            const productWithId = {
                                id: doc.id,
                                ...productData,
                                quantity: 0 // Para gestionar la cantidad en el carrito
                            };

                            // Verificar si tiene precios específicos para este restaurante
                            const restaurantAssignment = productData.restaurantAssignments.find(
                                ra => ra.restaurantId === placeId
                            );

                            if (restaurantAssignment && restaurantAssignment.customPrice) {
                                productWithId.price = restaurantAssignment.customPrice;
                            } else {
                                productWithId.price = productWithId.basePrice;
                            }

                            productsData.push(productWithId);

                            // Recopilar categorías únicas
                            if (productWithId.category) {
                                categoriesSet.add(productWithId.category);
                            }
                        }
                    }
                });

                console.log(`Se encontraron ${productsData.length} productos asignados a este restaurante`);

                // Ordenar por categoría y luego por nombre
                productsData.sort((a, b) => {
                    if (a.category !== b.category) {
                        return a.category?.localeCompare(b.category);
                    }
                    return a.name?.localeCompare(b.name);
                });

                setProducts(productsData);
                setFilteredProducts(productsData);

                // Convertir Set a Array y ordenar alfabéticamente
                const categoriesArray = Array.from(categoriesSet).sort();
                setCategories(categoriesArray);

                // Flag that we need to update products with cart quantities
                needsProductUpdate.current = true;

                setLoading(false);
            } catch (error) {
                console.error("Error fetching products:", error);
                setError("Error al cargar los productos. Por favor, intenta de nuevo.");
                setLoading(false);
            }
        };

        if (placeId) {
            fetchProducts();
        }
    }, [placeId, placeName]);

    // Escuchar cambios en el carrito en tiempo real
    useEffect(() => {
        if (!cartId) {
            console.log("No hay cartId, no se puede suscribir a cambios del carrito");
            return;
        }

        console.log("Suscribiéndose a cambios del carrito con ID:", cartId);

        // Referencia al carrito en Firestore
        const cartRef = doc(db, "carts", cartId);

        // Escuchar cambios en el carrito en tiempo real
        const unsubscribe = onSnapshot(
            cartRef,
            (docSnapshot) => {
                if (docSnapshot.exists()) {
                    const cartData = docSnapshot.data();
                    console.log("Datos del carrito actualizados:", cartData);

                    // Calcular el total y el recuento de elementos
                    let total = 0;
                    let itemCount = 0;

                    if (cartData.items && Array.isArray(cartData.items)) {
                        cartData.items.forEach(item => {
                            if (item.placeId === placeId) {
                                total += (item.price * item.quantity);
                                itemCount += item.quantity;
                            }
                        });
                    }

                    console.log(`Items en carrito para este restaurante: ${itemCount}, Total: ${total}`);

                    // Actualizar el estado del carrito
                    setCart({
                        items: cartData.items || [],
                        total: total
                    });

                    setCartItemCount(itemCount);

                    // Mark that we need to update product quantities
                    if (products.length > 0 && cartData.items && Array.isArray(cartData.items)) {
                        needsProductUpdate.current = true;
                    }
                } else {
                    console.log("El carrito no existe");
                    // Si el carrito no existe, inicializarlo vacío
                    setCart({ items: [], total: 0 });
                    setCartItemCount(0);

                    // Reset product quantities if needed
                    if (products.length > 0) {
                        needsProductUpdate.current = true;
                    }
                }
            },
            (error) => {
                console.error("Error listening to cart:", error);
                setError("Error al cargar el carrito. Por favor, recarga la página.");
            }
        );

        // Limpiar el listener cuando el componente se desmonte
        return () => unsubscribe();
    }, [cartId, placeId]); // Remove products from dependency array

    // Separate effect to update product quantities when cart changes
    useEffect(() => {
        if (needsProductUpdate.current && products.length > 0 && cart.items) {
            const updatedProducts = [...products];

            // Resetear todas las cantidades a 0
            updatedProducts.forEach(product => {
                product.quantity = 0;
            });

            // Actualizar cantidades según el carrito
            cart.items.forEach(item => {
                if (item.placeId === placeId) {
                    const productIndex = updatedProducts.findIndex(p => p.id === item.productId);
                    if (productIndex >= 0) {
                        updatedProducts[productIndex].quantity = item.quantity;
                    }
                }
            });

            setProducts(updatedProducts);

            // También actualizar productos filtrados si es necesario
            if (searchTerm || selectedCategory !== "all") {
                const updatedFilteredProducts = filteredProducts.map(product => {
                    const correspondingProduct = updatedProducts.find(p => p.id === product.id);
                    return correspondingProduct || product;
                });

                setFilteredProducts(updatedFilteredProducts);
            } else {
                setFilteredProducts(updatedProducts);
            }

            // Reset the update flag
            needsProductUpdate.current = false;
        }
    }, [products, cart.items, placeId, filteredProducts, searchTerm, selectedCategory]);

    // Filtrar productos basados en búsqueda y categoría
    useEffect(() => {
        let filtered = products;

        // Filtrar por categoría
        if (selectedCategory !== "all") {
            filtered = filtered.filter(product => product.category === selectedCategory);
        }

        // Filtrar por término de búsqueda
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.name?.toLowerCase().includes(term) ||
                product.description?.toLowerCase().includes(term)
            );
        }

        setFilteredProducts(filtered);
    }, [products, searchTerm, selectedCategory]);

    // Añadir producto al carrito
    const addToCart = async (product) => {
        if (!cartId) {
            console.error("No hay ID de carrito disponible");
            setError("Error: No se puede acceder al carrito");
            return;
        }

        setIsProcessing(true);
        console.log(`Añadiendo producto ${product.id} al carrito ${cartId}`);

        try {
            const cartRef = doc(db, "carts", cartId);

            // Verificar si el producto ya está en el carrito
            const existingItemIndex = cart.items.findIndex(
                item => item.productId === product.id && item.placeId === placeId
            );

            // Crear el objeto del item
            const cartItem = {
                productId: product.id,
                placeId: placeId,
                placeName: placeName,
                placeSlug: placeSlug,
                name: product.name,
                price: product.price,
                image: product.images && product.images.length > 0 ? product.images[0] : null,
                quantity: 1
            };

            if (existingItemIndex >= 0) {
                // El producto ya está en el carrito, actualizar cantidad
                console.log("Producto ya en carrito, aumentando cantidad");
                const updatedItems = [...cart.items];
                updatedItems[existingItemIndex].quantity += 1;

                await updateDoc(cartRef, {
                    items: updatedItems,
                    updatedAt: serverTimestamp()
                });
            } else {
                // El producto no está en el carrito, añadirlo
                console.log("Agregando nuevo producto al carrito");
                try {
                    // Intentar actualizar primero (asumiendo que el carrito ya existe)
                    await updateDoc(cartRef, {
                        items: arrayUnion(cartItem),
                        updatedAt: serverTimestamp()
                    });
                } catch (err) {
                    console.log("Error al actualizar, intentando crear nuevo carrito", err);
                    if (err.code === "not-found") {
                        // Si el carrito no existe, crearlo
                        await setDoc(cartRef, {
                            items: [cartItem],
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                            userId: user ? user.uid : null,
                            isAnonymous: !user
                        });
                    } else {
                        // Otro error
                        throw err;
                    }
                }
            }

            // Optimistic update
            const productIndex = products.findIndex(p => p.id === product.id);
            if (productIndex >= 0) {
                const updatedProducts = [...products];
                updatedProducts[productIndex] = {
                    ...updatedProducts[productIndex],
                    quantity: (updatedProducts[productIndex].quantity || 0) + 1
                };
                setProducts(updatedProducts);

                // Update filtered products if needed
                if (searchTerm || selectedCategory !== "all") {
                    const filteredIndex = filteredProducts.findIndex(p => p.id === product.id);
                    if (filteredIndex >= 0) {
                        const updatedFiltered = [...filteredProducts];
                        updatedFiltered[filteredIndex] = {
                            ...updatedFiltered[filteredIndex],
                            quantity: (updatedFiltered[filteredIndex].quantity || 0) + 1
                        };
                        setFilteredProducts(updatedFiltered);
                    }
                } else {
                    setFilteredProducts(updatedProducts);
                }
            }
        } catch (error) {
            console.error("Error adding to cart:", error);
            setError("Error al añadir el producto al carrito. Por favor, intenta de nuevo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Eliminar producto del carrito
    const removeFromCart = async (product) => {
        if (!cartId) {
            console.error("No hay ID de carrito disponible");
            return;
        }

        setIsProcessing(true);
        console.log(`Eliminando/reduciendo producto ${product.id} del carrito ${cartId}`);

        try {
            const cartRef = doc(db, "carts", cartId);

            // Encontrar el ítem en el carrito
            const existingItemIndex = cart.items.findIndex(
                item => item.productId === product.id && item.placeId === placeId
            );

            if (existingItemIndex >= 0) {
                const currentQuantity = cart.items[existingItemIndex].quantity;

                if (currentQuantity > 1) {
                    // Reducir la cantidad
                    console.log("Reduciendo cantidad del producto");
                    const updatedItems = [...cart.items];
                    updatedItems[existingItemIndex].quantity -= 1;

                    await updateDoc(cartRef, {
                        items: updatedItems,
                        updatedAt: serverTimestamp()
                    });
                } else {
                    // Eliminar el ítem si la cantidad es 1
                    console.log("Eliminando producto del carrito");
                    await updateDoc(cartRef, {
                        items: arrayRemove(cart.items[existingItemIndex]),
                        updatedAt: serverTimestamp()
                    });
                }

                // Optimistic update
                const productIndex = products.findIndex(p => p.id === product.id);
                if (productIndex >= 0 && products[productIndex].quantity > 0) {
                    const updatedProducts = [...products];
                    updatedProducts[productIndex] = {
                        ...updatedProducts[productIndex],
                        quantity: updatedProducts[productIndex].quantity - 1
                    };
                    setProducts(updatedProducts);

                    // Update filtered products if needed
                    if (searchTerm || selectedCategory !== "all") {
                        const filteredIndex = filteredProducts.findIndex(p => p.id === product.id);
                        if (filteredIndex >= 0 && filteredProducts[filteredIndex].quantity > 0) {
                            const updatedFiltered = [...filteredProducts];
                            updatedFiltered[filteredIndex] = {
                                ...updatedFiltered[filteredIndex],
                                quantity: updatedFiltered[filteredIndex].quantity - 1
                            };
                            setFilteredProducts(updatedFiltered);
                        }
                    } else {
                        setFilteredProducts(updatedProducts);
                    }
                }
            }
        } catch (error) {
            console.error("Error removing from cart:", error);
            setError("Error al eliminar el producto del carrito. Por favor, intenta de nuevo.");
        } finally {
            setIsProcessing(false);
        }
    };

    // Formatear precio
    const formatPrice = (price) => {
        return `${parseFloat(price).toFixed(2)}`;
    };

    // Obtener la cantidad del producto en el carrito
    const getProductQuantityInCart = (productId) => {
        const product = products.find(p => p.id === productId);
        return product ? product.quantity : 0;
    };

    // Renderizar componente
    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mt-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-purple-500" />
                    Menú y Productos
                </h2>

                {cartItemCount > 0 && (
                    <button
                        onClick={onCartOpen}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
                    >
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        <span className="font-medium">{cartItemCount}</span>
                        <span className="mx-1">•</span>
                        <span>{formatPrice(cart.total)}</span>
                    </button>
                )}
            </div>

            <div className="p-6">
                {/* Búsqueda y filtros */}
                <div className="mb-6 flex flex-col sm:flex-row gap-3">
                    <div className="relative sm:w-2/3">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar productos..."
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setSearchTerm("")}
                            >
                                <X className="h-4 w-4 text-gray-400 hover:text-gray-500" />
                            </button>
                        )}
                    </div>

                    {categories.length > 0 && (
                        <div className="sm:w-1/3">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Filter className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    <option value="all">Todas las categorías</option>
                                    {categories.map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Estado de carga */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader className="h-8 w-8 text-purple-500 animate-spin mb-4" />
                        <p className="text-gray-500">Cargando productos...</p>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 p-4 rounded-md mb-6">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-12 px-4">
                        <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-lg font-medium text-gray-900">
                            {searchTerm || selectedCategory !== "all"
                                ? "No se encontraron productos"
                                : "Este restaurante aún no tiene productos"}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm || selectedCategory !== "all"
                                ? "Prueba con otra búsqueda o categoría"
                                : "Vuelve pronto para ver las novedades"}
                        </p>
                    </div>
                ) : (
                    // Lista de productos
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {filteredProducts.map((product) => (
                            <div key={product.id} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                {/* Imagen del producto */}
                                <div className="h-48 overflow-hidden bg-gray-100">
                                    {product.images && product.images.length > 0 ? (
                                        <img
                                            src={product.images[0]}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Coffee className="h-12 w-12 text-gray-400" />
                                        </div>
                                    )}
                                </div>

                                {/* Información del producto */}
                                <div className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-base font-medium text-gray-900">{product.name}</h3>
                                        <span className="font-semibold text-purple-700">
                                            {formatPrice(product.price)}
                                        </span>
                                    </div>

                                    {product.category && (
                                        <div className="mb-2">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                                <Tag className="mr-1 h-3 w-3" />
                                                {product.category}
                                            </span>
                                        </div>
                                    )}

                                    {product.description && (
                                        <p className="text-sm text-gray-500 mb-4 line-clamp-3">{product.description}</p>
                                    )}

                                    {/* Controles de cantidad */}
                                    <div className="mt-2 flex items-center justify-between">
                                        {product.quantity > 0 ? (
                                            <div className="flex items-center">
                                                <button
                                                    onClick={() => removeFromCart(product)}
                                                    disabled={isProcessing}
                                                    className="inline-flex items-center justify-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                                                >
                                                    <Minus className="h-4 w-4" />
                                                </button>
                                                <span className="mx-3 text-gray-700 font-medium">
                                                    {product.quantity}
                                                </span>
                                                <button
                                                    onClick={() => addToCart(product)}
                                                    disabled={isProcessing}
                                                    className="inline-flex items-center justify-center p-1 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => addToCart(product)}
                                                disabled={isProcessing}
                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                                            >
                                                <Plus className="h-4 w-4 mr-1" />
                                                Agregar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductsSection;